// src/pages/appointments/edit/AppointmentEditMenu.jsx
import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import TopBarCenter from "@/sot/primitives/TopBarCenter.jsx";
import Card from "@/components/ui/Card.jsx";
import AppointmentDeleteDialog from "@/pages/appointments/details/AppointmentDeleteDialog.jsx";
import { loadPatientAppointmentSnapshot } from "@/services/patientAppointmentSnapshots.js";
import { getAppointmentLifecycleBucket } from "@/services/appointmentLifecycle.js";
import { resolveDoctorBookingEntryState } from "@/services/doctorBookingMode.js";
import { supabase } from "@/services/supabaseClient";
import { useLanguage } from "@/contexts/LanguageContext.jsx";

function removeAppointmentSnapshots(id) {
  try {
    sessionStorage.removeItem(`afrimed:appointment:${String(id)}`);
    // optional: also clear last snapshot if it matches this id (we don't know structure, so be safe)
    // sessionStorage.removeItem("afrimed:appointment:last");
  } catch {
    // ignore
  }
}

async function deleteAppointmentInDb(appointmentId) {
  const { error } = await supabase
    .from("appointments")
    .delete()
    .eq("id", appointmentId);
  if (error) throw error;
}

export default function AppointmentEditMenu() {
  const nav = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const { t } = useLanguage();

  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const [appointment, setAppointment] = React.useState(null);
  const [appointmentLoading, setAppointmentLoading] = React.useState(true);

  React.useEffect(() => {
    let alive = true;

    async function hydrateAppointment() {
      if (!id) {
        if (alive) setAppointmentLoading(false);
        return;
      }

      setAppointmentLoading(true);
      try {
        const snapshot = await loadPatientAppointmentSnapshot(id);
        if (!alive) return;
        setAppointment(snapshot || null);
      } catch {
        if (!alive) return;
        setAppointment(
          location.state && typeof location.state === "object" ? location.state : null
        );
      } finally {
        if (alive) setAppointmentLoading(false);
      }
    }

    hydrateAppointment();
    return () => {
      alive = false;
    };
  }, [id, location.state]);

  const lifecycleBucket = React.useMemo(
    () => getAppointmentLifecycleBucket(appointment || {}, Date.now()),
    [appointment]
  );
  const isPastAppointment = lifecycleBucket === "past";

  const rows = React.useMemo(
    () =>
      isPastAppointment
        ? [
            {
              key: "rebook",
              title: t("appointments.past.details.bookAgain", "Book again"),
              subtitle: t(
                "appointments.editMenu.rebook.subtitle",
                "Start a new booking with this doctor"
              ),
              icon: "/icons/appointments/calendar_clock.svg",
            },
            {
              key: "delete",
              title: t("appointments.editMenu.delete.title", "Delete appointment"),
              subtitle: t(
                "appointments.editMenu.delete.subtitle",
                "This will remove this appointment"
              ),
              icon: "/icons/doctor/delete_critical.svg",
              danger: true,
            },
          ]
        : [
            {
              key: "change",
              title: t("appointments.editMenu.change.title", "Change appointment"),
              subtitle: t("appointments.editMenu.change.subtitle", "Select a new date and time"),
              icon: "/icons/appointments/calendar_clock.svg",
            },
            {
              key: "location",
              title: t("appointments.editMenu.location.title", "Change location"),
              subtitle: t("appointments.editMenu.location.subtitle", "Choose a facility"),
              icon: "/icons/appointments/hospital_Fill.svg",
            },
            {
              key: "delete",
              title: t("appointments.editMenu.delete.title", "Delete appointment"),
              subtitle: t(
                "appointments.editMenu.delete.subtitle",
                "This will remove this appointment"
              ),
              icon: "/icons/doctor/delete_critical.svg",
              danger: true,
            },
          ],
    [isPastAppointment, t]
  );

  async function handleRebookPastAppointment() {
    const doctorId =
      String(
        appointment?.doctor?.id ||
          appointment?.doctor_id ||
          appointment?.doctorId ||
          ""
      ).trim();
    if (!doctorId) return;

    if (appointment?.mode === "inperson") {
      nav(`/appointments/edit-location/${id}`, {
        state: {
          __flow: "rebook",
          fromPastAppointmentId: id,
          doctor: appointment?.doctor || null,
          mode: "inperson",
          facilityId: appointment?.facilityId || null,
          facilityName: appointment?.facilityName || "",
          facility: appointment?.facility || null,
        },
      });
      return;
    }

    const resolved = await resolveDoctorBookingEntryState({
      doctorId,
      doctor: appointment?.doctor || null,
      requestedMode: "evisit",
      consultationType: appointment?.consultation_type || null,
      facilityId: appointment?.facilityId || null,
      facilityName: appointment?.facilityName || "",
      facility: appointment?.facility || null,
    });

    nav(`/appointments/book/${doctorId}`, {
      state: {
        doctor: appointment?.doctor || null,
        mode: resolved.mode,
        facilityId: resolved.facilityId,
        facilityName: resolved.facilityName,
        facility: resolved.facility,
        __flow: "rebook",
        fromPastAppointmentId: id,
      },
    });
  }

  return (
    <div className="min-h-[100dvh] bg-[rgb(var(--clr-surface-container-low))]" data-hide-tabbar="true">
      <div className="afrimed-rail afrimed-rail--comfortable pt-4 pb-24">
        <TopBarCenter
          title={
            isPastAppointment
              ? t("appointments.past.details.title", "Past appointments")
              : t("appointments.edit.title", "Edit appointments")
          }
          close
          onBack={() => nav(-1)}
        />

        {appointmentLoading ? (
          <Card className="mt-6 px-4 py-4 text-body-medium text-on-surface-var">
            {t("appointments.edit.loading", "Loading appointment...")}
          </Card>
        ) : null}

        <div className="mt-6 space-y-4">
          {rows.map((r) => (
            <Card
              as="button"
              key={r.key}
              type="button"
              className="
                w-full text-left
                px-4 py-4
                flex items-center gap-4
              "
              onClick={() => {
                if (r.key === "rebook") {
                  handleRebookPastAppointment();
                  return;
                }
                if (r.key === "change") {
                  nav(`/appointments/edit/${id}`, { state: location.state || null });
                  return;
                }
                if (r.key === "location") {
                  nav(`/appointments/edit-location/${id}`, { state: location.state || null });
                  return;
                }
                if (r.key === "delete") {
                  setDeleteOpen(true);
                }
              }}
            >
              {/* icon (NO background) */}
              <img src={r.icon} alt="" className="h-7 w-7 opacity-90" draggable="false" />

              <div className="flex-1 min-w-0">
                <div
                  className={[
                    "text-body-large",
                    r.danger ? "text-[rgb(var(--schemes-critical))]" : "text-on-surface",
                  ].join(" ")}
                >
                  {r.title}
                </div>
                <div className="text-body-medium text-on-surface-var mt-0.5">{r.subtitle}</div>
              </div>

              <span className="text-on-surface-var text-title-medium">›</span>
            </Card>
          ))}
        </div>
      </div>

      <AppointmentDeleteDialog
        open={deleteOpen}
        title={t(
          "appointments.editMenu.deleteDialog.title",
          "Are you sure you want to delete this medical appointment?"
        )}
        description={t(
          "appointments.editMenu.deleteDialog.description",
          "After deleting the medical appointment record, you will not be able to recover it."
        )}
        cancelText={t("common.no", "No")}
        confirmText={t("appointments.editMenu.deleteDialog.confirm", "Delete")}
        onCancel={() => setDeleteOpen(false)}
        onConfirm={() => {
          (async () => {
            setDeleteOpen(false);
            try {
              await deleteAppointmentInDb(id);
            } catch {
              // Keep local cleanup + navigation fallback even if DB delete fails.
            }

            removeAppointmentSnapshots(id);
            try {
              sessionStorage.setItem("afrimed:toast:appointments:deleted", "1");
              localStorage.setItem("afrimed:toast:appointments:deleted", "1");
            } catch {
              // ignore
            }
            window.dispatchEvent(new Event("afrimed:appointments:changed"));
            nav("/appointments?toast=deleted", { replace: true });
          })();
        }}
      />
    </div>
  );
}
